import { ArticleOutlined, CopyAllOutlined } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ETheme } from "../../../shared/enums/user/ETheme";
import { formatDate, isDateInPast } from "../../../shared/functions/formatDate";
import { getClientStatusColor } from "../../../shared/functions/getChipColors";
import { EditIcon, TrashIcon } from "../../../shared/icons/Icons";
import { IClient } from "../../../shared/interfaces/client/IClient";
import { lightPalette } from "../../../shared/mui/palettes";
import { darkTheme, lightTheme } from "../../../shared/mui/theme";
import { RootState } from "../../../store/store";

export const useClientColumns = (
  setSelectedClient: (client: IClient) => void,
  setShowDeleteModal: (show: boolean) => void,
  hideActionColumn = false,
): GridColDef[] => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  return [
    { field: "id", headerName: t("general.id"), width: 80 },
    {
      field: "name",
      headerName: t("client.name"),
      flex: 1,
      minWidth: 250,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={() => {
            navigate(`/client/update/${params?.row?.id}`);
            setSelectedClient(params?.row);
          }}
        >
          {params.value}
        </div>
      ),
    },

    {
      field: "apiKey",
      headerName: t("general.apiKey"),
      width: 100,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div
          style={{ display: "flex", alignItems: "center", height: "100%" }}
          className={"cursor-pointer"}
          onClick={async () => {
            enqueueSnackbar(
              t("toasts.successfullyCopied", {
                title: `${params?.row?.name} API Key`,
              }),
              {
                variant: "success",
              },
            );
            await navigator.clipboard.writeText(params?.value);
          }}
        >
          {params?.value && (
            <div style={{ display: "flex" }}>
              <CopyAllOutlined />
            </div>
          )}
        </div>
      ),
    },
    {
      field: "email",
      headerName: t("general.email"),
      width: 250,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "expirationDate",
      headerName: t("general.expirationDate"),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: isDateInPast(params?.value)
              ? lightPalette.error.main
              : undefined,
          }}
        >
          {formatDate(params.value)}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: t("general.status"),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`client.status.${params.value}`)}
          color={getClientStatusColor(params.value)}
          variant="outlined"
        />
      ),
    },
    {
      field: "teamSize",
      headerName: t("general.teamSize"),
      width: 100,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {t(`client.teamSize.${params?.value}`)}
        </div>
      ),
    },

    {
      field: "actions",
      headerName: t("general.action"),
      width: 150,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Box
          sx={{
            display: hideActionColumn ? "none" : "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {/* Edit or View Icon */}
          <span
            style={{ cursor: "pointer", display: "flex" }}
            onClick={() => {
              navigate(`/client/update/${params?.row?.id}`);
            }}
          >
            <EditIcon
              fill={
                userData?.theme === ETheme.LIGHT
                  ? lightTheme.palette.text.primary
                  : darkTheme.palette.text.primary
              }
            />
          </span>

          <span
            style={{ cursor: "pointer" }}
            className={"ms-3 d-flex"}
            onClick={() => {
              setSelectedClient(params.row);
              setShowDeleteModal(true);
            }}
          >
            <TrashIcon
              fill={
                userData?.theme === ETheme.LIGHT
                  ? lightTheme.palette.text.primary
                  : darkTheme.palette.text.primary
              }
            />
          </span>

          <span
            style={{ cursor: "pointer", display: "flex", marginLeft: "12px" }}
            onClick={() => navigate(`/client/offer/${params.row?.id}`)}
            title="View offer"
          >
            <ArticleOutlined
              sx={{
                fontSize: 20,
                color:
                  userData?.theme === ETheme.LIGHT
                    ? lightTheme.palette.text.primary
                    : darkTheme.palette.text.primary,
              }}
            />
          </span>
        </Box>
      ),
    },
  ];
};
